import type { NormalizedBBox } from 'features/knowledgeDocumentViewer/types/ocrViewer'
import { useGeckoPermissions } from 'features/permissions'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  compareRunToLabels,
  type ComparisonCategory,
  type ComparisonResult,
} from './comparison'
import {
  type PublishedRecord,
  recordsToPageSlice,
  recordsToWorkingCopy,
} from './liveList'
import type {
  LabeledBox,
  LabelerOcrBlock,
  LabelerPrediction,
  LabelerTaxonomy,
  NormalizedBox,
  PageLabels,
  SourceListSummary,
} from './types'
import {
  useLabelerPage,
  useLabelerPageOcr,
  useLabelerPageRuns,
  useLabelerPredictions,
  useLabelerService,
  useLabelerTaxonomy,
  useSiteSourceLists,
  useSourceListPageRecords,
} from './useLabelerService'
import {
  addDrawnBox,
  buildRecordsDocument,
  bulkSetType,
  clearNames,
  clearPage,
  deleteBoxes,
  emptyPage,
  groupBoxes,
  hasHumanTouch,
  loadWorkingCopy,
  saveWorkingCopy,
  seedFromPredictions,
  snapToOcr,
  toggleVerified,
  toggleVerifiedMany,
  updateBox,
  verifyAll,
  type WorkingCopy,
} from './workingCopy'

export interface LabelerSyncStatus {
  state: 'idle' | 'dirty' | 'syncing' | 'synced' | 'error'
  lastSyncedAt: string | null
  error: string | null
}

export type BoxVerifiedFilter = 'all' | 'verified' | 'unverified'

interface LabelerContextValue {
  active: boolean
  siteId: string
  bqDocumentId: string | undefined
  pageId: string | undefined
  pageNumber: number

  taxonomy: LabelerTaxonomy | undefined
  predictions: LabelerPrediction[] | undefined
  predictionsCache: Record<string, LabelerPrediction[]>
  isLoadingPredictions: boolean
  /** Non-null when the seed source failed to LOAD — distinct from a source
   * that loaded fine and has nothing on this page. */
  predictionsError: string | null
  runs: ReturnType<typeof useLabelerPageRuns>['data']
  pageMeta: ReturnType<typeof useLabelerPage>['data']
  /** `list:<id>` or `run:<id>`; null while the default resolves. */
  seedSource: string | null
  setSeedSource: (source: string | null) => void
  seedLists: SourceListSummary[]

  page: PageLabels | undefined
  workingCopy: WorkingCopy
  isSeeded: boolean
  seed: () => void
  draw: (bbox: NormalizedBox) => void
  update: (boxId: string, updates: Parameters<typeof updateBox>[2]) => void
  remove: (boxIds: Set<string>) => void
  toggleBoxVerified: (boxId: string) => void
  toggleBoxVerifiedMany: (boxIds: Set<string>) => void
  confirmAll: () => void
  retypeSelected: (type: string | null) => void
  clearDescriptionsSelected: () => void
  clearDescription: (boxId: string) => void
  groupSelected: (delimiter: string) => void
  clear: () => void
  setComplete: (complete: boolean) => void

  liveBbox: { boxId: string; bbox: NormalizedBox } | null
  setLiveBbox: (live: { boxId: string; bbox: NormalizedBox } | null) => void
  gestureDraft: {
    kind: 'draw' | 'select'
    rect: { x: number; y: number; width: number; height: number }
  } | null
  setGestureDraft: (draft: LabelerContextValue['gestureDraft']) => void
  commitBbox: (boxId: string, bbox: NormalizedBox) => void
  rubberBandSelect: (bbox: NormalizedBox) => void

  selectedBoxId: string | null
  selectBox: (boxId: string | null, opts?: { zoom?: boolean }) => void
  selectedBoxIds: Set<string>
  setSelectedBoxIds: React.Dispatch<React.SetStateAction<Set<string>>>
  visibleBoxes: LabeledBox[]
  boxTextFilter: string
  setBoxTextFilter: (v: string) => void
  boxTypeFilter: string
  setBoxTypeFilter: (v: string) => void
  boxVerifiedFilter: BoxVerifiedFilter
  setBoxVerifiedFilter: (v: BoxVerifiedFilter) => void
  boxDescFilter: 'all' | 'has' | 'none'
  setBoxDescFilter: (v: 'all' | 'has' | 'none') => void

  isDrawingMode: boolean
  setIsDrawingMode: (on: boolean) => void

  syncStatus: LabelerSyncStatus
  syncNow: () => Promise<void>
  isHydrating: boolean

  compareMode: boolean
  setCompareMode: (on: boolean) => void
  iouThreshold: number
  setIouThreshold: (v: number) => void
  compareVerifiedOnly: boolean
  setCompareVerifiedOnly: (v: boolean) => void
  compareCategoryFilter: ComparisonCategory | null
  setCompareCategoryFilter: (c: ComparisonCategory | null) => void
  comparison: ComparisonResult | null
  selectedComparisonId: string | null
  selectComparisonItem: (id: string | null, opts?: { zoom?: boolean }) => void
}

const LabelerContext = createContext<LabelerContextValue | null>(null)

export function useLabeler(): LabelerContextValue {
  const ctx = useContext(LabelerContext)
  if (!ctx) throw new Error('useLabeler requires a LabelerProvider')
  return ctx
}

/** Renders-with-or-without variant: null instead of throwing. */
export function useLabelerOptional(): LabelerContextValue | null {
  return useContext(LabelerContext)
}

function bboxToNormalizedBBox(bbox: NormalizedBox): NormalizedBBox {
  return {
    left: bbox.x_min,
    top: bbox.y_min,
    width: bbox.x_max - bbox.x_min,
    height: bbox.y_max - bbox.y_min,
  }
}

export const LabelerProvider: React.FC<{
  active: boolean
  siteId: string
  bqDocumentId: string | undefined
  pageNumber: number
  onZoomRequest?: (bbox: NormalizedBBox) => void
  children: React.ReactNode
}> = ({
  active,
  siteId,
  bqDocumentId,
  pageNumber,
  onZoomRequest,
  children,
}) => {
  const pageId = bqDocumentId ? `${bqDocumentId}_pg_${pageNumber}` : undefined
  const fetchSite = active ? siteId : undefined

  const { data: taxonomy } = useLabelerTaxonomy(fetchSite)
  const { data: runs } = useLabelerPageRuns(fetchSite, pageId)
  const { data: pageMeta } = useLabelerPage(fetchSite, pageId)

  const [seedSource, setSeedSource] = useState<string | null>(null)
  const { data: siteLists } = useSiteSourceLists(fetchSite)
  const { user: permsUser } = useGeckoPermissions()
  const seedLists = useMemo(
    () =>
      (siteLists ?? []).filter(
        (l) =>
          !(
            l.adapter === 'manual_labels' &&
            (l.params as { user?: string })?.user === permsUser?.email
          )
      ),
    [siteLists, permsUser?.email]
  )
  useEffect(() => {
    if (seedSource !== null || seedLists.length === 0) return
    const newest = [...seedLists].sort((a, b) =>
      (b.created_at ?? '').localeCompare(a.created_at ?? '')
    )[0]
    if (newest) setSeedSource(`list:${newest.id}`)
  }, [seedSource, seedLists])

  const seedListId = seedSource?.startsWith('list:')
    ? seedSource.slice(5)
    : null
  const seedRunId = seedSource?.startsWith('run:') ? seedSource.slice(4) : null

  const {
    data: bqPredictions,
    isLoading: isLoadingBqPredictions,
    error: bqPredictionsError,
  } = useLabelerPredictions(
    seedListId ? undefined : fetchSite,
    pageId,
    seedRunId
  )
  const {
    data: listRecordsPage,
    isLoading: isLoadingListRecords,
    error: listRecordsError,
  } = useSourceListPageRecords(fetchSite, seedListId, pageId)

  const predictions = useMemo(() => {
    if (seedListId) {
      if (!listRecordsPage || !pageId) return undefined
      return recordsToPageSlice(
        listRecordsPage.records as PublishedRecord[],
        pageId,
        seedListId
      )
    }
    return bqPredictions
  }, [seedListId, listRecordsPage, pageId, bqPredictions])
  const isLoadingPredictions = seedListId
    ? isLoadingListRecords
    : isLoadingBqPredictions
  const predictionsError: string | null = seedListId
    ? listRecordsError
      ? String(
          (listRecordsError as { message?: string }).message ?? listRecordsError
        )
      : null
    : bqPredictionsError
      ? String(
          (bqPredictionsError as { message?: string }).message ??
            bqPredictionsError
        )
      : null

  const [predictionsCache, setPredictionsCache] = useState<
    Record<string, LabelerPrediction[]>
  >({})
  useEffect(() => {
    if (pageId && predictions) {
      setPredictionsCache((cache) =>
        cache[pageId] === predictions
          ? cache
          : { ...cache, [pageId]: predictions }
      )
    }
  }, [pageId, predictions])

  const [workingCopy, setWorkingCopy] = useState<WorkingCopy>(() =>
    loadWorkingCopy(siteId)
  )
  useEffect(() => {
    skipNextSyncRef.current = true
    setWorkingCopy(loadWorkingCopy(siteId))
  }, [siteId])
  useEffect(() => {
    saveWorkingCopy(siteId, workingCopy)
  }, [siteId, workingCopy])

  const { publishManualList, putVerdicts, listSourceLists, listRecords } =
    useLabelerService()
  const { user } = useGeckoPermissions()
  const userEmail = user?.email

  const [syncStatus, setSyncStatus] = useState<LabelerSyncStatus>({
    state: 'idle',
    lastSyncedAt: null,
    error: null,
  })
  const [isHydrating, setIsHydrating] = useState(false)
  const hydratedSitesRef = useRef<Set<string>>(new Set())
  const skipNextSyncRef = useRef(true)
  const syncingRef = useRef(false)
  const resyncQueuedRef = useRef(false)
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const workingCopyRef = useRef(workingCopy)
  workingCopyRef.current = workingCopy
  const taxonomyRef = useRef(taxonomy)
  taxonomyRef.current = taxonomy
  const predictionsCacheRef = useRef(predictionsCache)
  predictionsCacheRef.current = predictionsCache

  useEffect(() => {
    if (!active || !userEmail || hydratedSitesRef.current.has(siteId)) return
    if (Object.keys(loadWorkingCopy(siteId)).length > 0) {
      hydratedSitesRef.current.add(siteId)
      return
    }
    let cancelled = false
    setIsHydrating(true)
    ;(async () => {
      try {
        const lists = await listSourceLists(siteId)
        const mine = lists.find(
          (l) =>
            l.adapter === 'manual_labels' &&
            (l.params as { user?: string })?.user === userEmail
        )
        if (mine && !cancelled) {
          const { records } = await listRecords(siteId, mine.id)
          const labeledPages = ((mine.params as { labeled_pages?: string[] })
            ?.labeled_pages ?? []) as string[]
          const hydrated = recordsToWorkingCopy(
            records as PublishedRecord[],
            siteId,
            labeledPages
          )
          if (!cancelled && Object.keys(hydrated).length > 0) {
            skipNextSyncRef.current = true
            setWorkingCopy(hydrated)
            setSyncStatus({
              state: 'synced',
              lastSyncedAt:
                (mine as { updated_at?: string }).updated_at ?? null,
              error: null,
            })
          }
        }
      } catch {
        /* nothing published yet, or transient — labeling proceeds locally */
      } finally {
        if (!cancelled) {
          hydratedSitesRef.current.add(siteId)
          setIsHydrating(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [active, siteId, userEmail, listSourceLists, listRecords])

  const lastSyncedFingerprintRef = useRef<string | null>(null)

  const buildSyncPayload = useCallback(() => {
    const sitePages = Object.values(workingCopyRef.current).filter(
      (p) => p.siteId === siteId && hasHumanTouch(p)
    )
    const { records } = buildRecordsDocument(sitePages, taxonomyRef.current)
    const labeledPages = sitePages
      .filter((p) => p.complete)
      .map((p) => p.pageId)
    return {
      sitePages,
      records,
      labeledPages,
      fingerprint: JSON.stringify({ records, labeledPages }),
    }
  }, [siteId])

  const syncNow = useCallback(async () => {
    if (syncingRef.current) {
      resyncQueuedRef.current = true
      return
    }
    if (syncTimerRef.current) {
      clearTimeout(syncTimerRef.current)
      syncTimerRef.current = null
    }
    const { sitePages, records, labeledPages, fingerprint } = buildSyncPayload()
    if (records.length === 0) return
    if (fingerprint === lastSyncedFingerprintRef.current) {
      setSyncStatus((s) =>
        s.state === 'dirty' ? { ...s, state: 'synced' } : s
      )
      return
    }
    syncingRef.current = true
    setSyncStatus((s) => ({ ...s, state: 'syncing', error: null }))
    try {
      const summary = await publishManualList({
        site: siteId,
        labeledPages,
        inProgress: sitePages.some((p) => !p.complete),
        recordsDocument: { records },
      })
      const cache = predictionsCacheRef.current
      const judged = sitePages.filter(
        (p) =>
          p.seedRunId &&
          (p.boxes.some((b) => b.verified) || p.rejections.length > 0) &&
          cache[p.pageId]
      )
      if (judged.length > 0) {
        await putVerdicts({
          site: siteId,
          pages: judged.map((p) => ({
            page_id: p.pageId,
            document_id: p.documentId,
            page_num: p.pageNum,
            run_id: p.seedRunId,
            complete: p.complete,
            predictions: cache[p.pageId] ?? [],
            boxes: p.boxes,
            rejections: p.rejections,
          })),
        })
      }
      lastSyncedFingerprintRef.current = fingerprint
      setSyncStatus({
        state: 'synced',
        lastSyncedAt:
          (summary.updated_at as string | null | undefined) ??
          new Date().toISOString(),
        error: null,
      })
    } catch (err) {
      setSyncStatus((s) => ({
        ...s,
        state: 'error',
        error: err instanceof Error ? err.message : String(err),
      }))
    } finally {
      syncingRef.current = false
      if (resyncQueuedRef.current) {
        resyncQueuedRef.current = false
        syncTimerRef.current = setTimeout(() => void syncNow(), 1000)
      }
    }
  }, [siteId, buildSyncPayload, publishManualList, putVerdicts])

  const syncNowRef = useRef(syncNow)
  syncNowRef.current = syncNow

  useEffect(() => {
    if (!active || !hydratedSitesRef.current.has(siteId)) return
    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false
      return
    }
    setSyncStatus((s) => (s.state === 'syncing' ? s : { ...s, state: 'dirty' }))
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current)
    syncTimerRef.current = setTimeout(() => void syncNowRef.current(), 4000)
    return () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current)
    }
  }, [workingCopy, active, siteId])

  const page = pageId ? workingCopy[pageId] : undefined
  const isSeeded = !!page && (page.boxes.length > 0 || page.seedRunId !== null)

  const mutatePage = useCallback(
    (fn: (page: PageLabels) => PageLabels) => {
      if (!pageId || !bqDocumentId) return
      setWorkingCopy((copy) => {
        const current =
          copy[pageId] ??
          emptyPage({
            pageId,
            siteId,
            documentId: bqDocumentId,
            pageNum: pageNumber,
          })
        return { ...copy, [pageId]: fn(current) }
      })
    },
    [pageId, bqDocumentId, siteId, pageNumber]
  )

  const [selectedBoxId, setSelectedBoxId] = useState<string | null>(null)
  const [selectedBoxIds, setSelectedBoxIds] = useState<Set<string>>(new Set())
  const [boxTextFilter, setBoxTextFilter] = useState('')
  const [boxTypeFilter, setBoxTypeFilter] = useState('all')
  const [boxVerifiedFilter, setBoxVerifiedFilter] =
    useState<BoxVerifiedFilter>('all')
  const [boxDescFilter, setBoxDescFilter] = useState<'all' | 'has' | 'none'>(
    'all'
  )
  const [isDrawingMode, setIsDrawingMode] = useState(false)

  useEffect(() => {
    setSelectedBoxId(null)
    setSelectedBoxIds(new Set())
    setIsDrawingMode(false)
  }, [pageId])

  useEffect(() => {
    setSelectedBoxId(null)
    setSelectedBoxIds(new Set())
  }, [boxTextFilter, boxTypeFilter, boxVerifiedFilter, boxDescFilter])

  const visibleBoxes = useMemo(() => {
    const boxes = page?.boxes ?? []
    const text = boxTextFilter.trim().toLowerCase()
    return boxes.filter((b) => {
      if (text && !b.text.toLowerCase().includes(text)) return false
      if (boxTypeFilter === 'untyped') {
        if (b.type) return false
      } else if (boxTypeFilter !== 'all' && b.type !== boxTypeFilter) {
        return false
      }
      if (boxVerifiedFilter === 'verified' && !b.verified) return false
      if (boxVerifiedFilter === 'unverified' && b.verified) return false
      if (boxDescFilter === 'has' && !b.name) return false
      if (boxDescFilter === 'none' && b.name) return false
      return true
    })
  }, [
    page?.boxes,
    boxTextFilter,
    boxTypeFilter,
    boxVerifiedFilter,
    boxDescFilter,
  ])

  const selectBox = useCallback(
    (boxId: string | null, opts?: { zoom?: boolean }) => {
      setSelectedBoxId(boxId)
      if (!boxId || opts?.zoom === false) return
      const box = (page?.boxes ?? []).find((b) => b.id === boxId)
      if (box) {
        onZoomRequest?.(bboxToNormalizedBBox(box.bbox))
      }
    },
    [page?.boxes, onZoomRequest]
  )

  const seed = useCallback(() => {
    if (!predictions) return
    mutatePage((p) => seedFromPredictions(p, predictions, taxonomy))
  }, [mutatePage, predictions, taxonomy])

  const { data: ocrBlocks } = useLabelerPageOcr(fetchSite, pageId)
  const ocrBlocksRef = useRef<LabelerOcrBlock[] | undefined>(ocrBlocks)
  ocrBlocksRef.current = ocrBlocks

  const draw = useCallback(
    (bbox: NormalizedBox) => {
      const snapped = snapToOcr(bbox, ocrBlocksRef.current)
      mutatePage((p) => addDrawnBox(p, snapped.bbox, snapped.text))
      setIsDrawingMode(false)
    },
    [mutatePage]
  )

  const lastBoxCount = useRef(0)
  useEffect(() => {
    const boxes = page?.boxes ?? []
    if (boxes.length === lastBoxCount.current + 1) {
      const last = boxes[boxes.length - 1]
      if (last.origin === 'drawn' && !last.text) {
        setSelectedBoxId(last.id)
      }
    }
    lastBoxCount.current = boxes.length
  }, [page?.boxes])

  const update = useCallback(
    (boxId: string, updates: Parameters<typeof updateBox>[2]) =>
      mutatePage((p) => updateBox(p, boxId, updates)),
    [mutatePage]
  )

  const remove = useCallback(
    (boxIds: Set<string>) => {
      mutatePage((p) => deleteBoxes(p, boxIds))
      setSelectedBoxId((cur) => (cur && boxIds.has(cur) ? null : cur))
      setSelectedBoxIds((cur) => {
        const next = new Set(cur)
        boxIds.forEach((id) => next.delete(id))
        return next
      })
    },
    [mutatePage]
  )

  const toggleBoxVerified = useCallback(
    (boxId: string) => mutatePage((p) => toggleVerified(p, boxId)),
    [mutatePage]
  )

  const toggleBoxVerifiedMany = useCallback(
    (boxIds: Set<string>) => mutatePage((p) => toggleVerifiedMany(p, boxIds)),
    [mutatePage]
  )

  const confirmAll = useCallback(
    () => mutatePage((p) => verifyAll(p)),
    [mutatePage]
  )

  const retypeSelected = useCallback(
    (type: string | null) => {
      const ids =
        selectedBoxIds.size > 0
          ? selectedBoxIds
          : selectedBoxId
            ? new Set([selectedBoxId])
            : null
      if (!ids) return
      mutatePage((p) => bulkSetType(p, ids, type))
      setSelectedBoxIds(new Set())
    },
    [mutatePage, selectedBoxIds, selectedBoxId]
  )

  const clearDescriptionsSelected = useCallback(() => {
    const ids =
      selectedBoxIds.size > 0
        ? selectedBoxIds
        : selectedBoxId
          ? new Set([selectedBoxId])
          : null
    if (!ids) return
    mutatePage((p) => clearNames(p, ids))
  }, [mutatePage, selectedBoxIds, selectedBoxId])

  const clearDescription = useCallback(
    (boxId: string) => mutatePage((p) => clearNames(p, new Set([boxId]))),
    [mutatePage]
  )

  const groupSelected = useCallback(
    (delimiter: string) => {
      if (selectedBoxIds.size < 2) return
      mutatePage((p) => groupBoxes(p, selectedBoxIds, delimiter))
      setSelectedBoxIds(new Set())
      setSelectedBoxId(null)
    },
    [mutatePage, selectedBoxIds]
  )

  const clear = useCallback(() => mutatePage(clearPage), [mutatePage])

  const [gestureDraft, setGestureDraft] = useState<{
    kind: 'draw' | 'select'
    rect: { x: number; y: number; width: number; height: number }
  } | null>(null)

  const [liveBbox, setLiveBbox] = useState<{
    boxId: string
    bbox: NormalizedBox
  } | null>(null)

  const commitBbox = useCallback(
    (boxId: string, bbox: NormalizedBox) =>
      mutatePage((p) => updateBox(p, boxId, { bbox })),
    [mutatePage]
  )

  const rubberBandSelect = useCallback(
    (bbox: NormalizedBox) => {
      const hits = visibleBoxes
        .filter(
          (b) =>
            b.bbox.x_max > bbox.x_min &&
            b.bbox.x_min < bbox.x_max &&
            b.bbox.y_max > bbox.y_min &&
            b.bbox.y_min < bbox.y_max
        )
        .map((b) => b.id)
      if (hits.length === 0) return
      setSelectedBoxIds((cur) => {
        const next = new Set(cur)
        if (selectedBoxId) next.add(selectedBoxId)
        hits.forEach((id) => next.add(id))
        return next
      })
      setSelectedBoxId(null)
    },
    [visibleBoxes, selectedBoxId]
  )

  const setComplete = useCallback(
    (complete: boolean) =>
      mutatePage((p) => ({
        ...p,
        complete,
        updatedAt: new Date().toISOString(),
      })),
    [mutatePage]
  )

  const [compareMode, setCompareMode] = useState(false)
  const [iouThreshold, setIouThreshold] = useState(0.5)
  const [compareVerifiedOnly, setCompareVerifiedOnly] = useState(true)
  const [compareCategoryFilter, setCompareCategoryFilter] =
    useState<ComparisonCategory | null>(null)
  const [selectedComparisonId, setSelectedComparisonId] = useState<
    string | null
  >(null)

  useEffect(() => {
    setSelectedComparisonId(null)
    setCompareCategoryFilter(null)
  }, [pageId, compareMode])

  const comparison = useMemo(() => {
    if (!compareMode || !predictions || !page) return null
    return compareRunToLabels({
      predictions,
      gtBoxes: page.boxes,
      taxonomy,
      iouThreshold,
      verifiedOnly: compareVerifiedOnly,
    })
  }, [
    compareMode,
    predictions,
    page,
    taxonomy,
    iouThreshold,
    compareVerifiedOnly,
  ])

  const selectComparisonItem = useCallback(
    (id: string | null, opts?: { zoom?: boolean }) => {
      setSelectedComparisonId((cur) => (cur === id ? null : id))
      if (!id || opts?.zoom === false) return
      const item = comparison?.items.find((i) => i.id === id)
      if (item) onZoomRequest?.(bboxToNormalizedBBox(item.bbox))
    },
    [comparison, onZoomRequest]
  )

  const value = useMemo<LabelerContextValue>(
    () => ({
      active,
      siteId,
      bqDocumentId,
      pageId,
      pageNumber,
      taxonomy,
      predictions,
      predictionsCache,
      isLoadingPredictions,
      predictionsError,
      runs,
      pageMeta,
      seedSource,
      setSeedSource,
      seedLists,
      page,
      workingCopy,
      isSeeded,
      seed,
      draw,
      update,
      remove,
      toggleBoxVerified,
      toggleBoxVerifiedMany,
      confirmAll,
      retypeSelected,
      clearDescriptionsSelected,
      clearDescription,
      groupSelected,
      clear,
      setComplete,
      liveBbox,
      setLiveBbox,
      gestureDraft,
      setGestureDraft,
      commitBbox,
      rubberBandSelect,
      selectedBoxId,
      selectBox,
      selectedBoxIds,
      setSelectedBoxIds,
      visibleBoxes,
      boxTextFilter,
      setBoxTextFilter,
      boxTypeFilter,
      setBoxTypeFilter,
      boxVerifiedFilter,
      setBoxVerifiedFilter,
      boxDescFilter,
      setBoxDescFilter,
      isDrawingMode,
      setIsDrawingMode,
      syncStatus,
      syncNow,
      isHydrating,
      compareMode,
      setCompareMode,
      iouThreshold,
      setIouThreshold,
      compareVerifiedOnly,
      setCompareVerifiedOnly,
      compareCategoryFilter,
      setCompareCategoryFilter,
      comparison,
      selectedComparisonId,
      selectComparisonItem,
    }),
    [
      active,
      siteId,
      bqDocumentId,
      pageId,
      pageNumber,
      taxonomy,
      predictions,
      predictionsCache,
      isLoadingPredictions,
      predictionsError,
      runs,
      pageMeta,
      seedSource,
      seedLists,
      page,
      workingCopy,
      isSeeded,
      seed,
      draw,
      update,
      remove,
      toggleBoxVerified,
      toggleBoxVerifiedMany,
      confirmAll,
      retypeSelected,
      clearDescriptionsSelected,
      clearDescription,
      groupSelected,
      clear,
      setComplete,
      liveBbox,
      gestureDraft,
      commitBbox,
      rubberBandSelect,
      selectedBoxId,
      selectBox,
      selectedBoxIds,
      visibleBoxes,
      boxTextFilter,
      boxTypeFilter,
      boxVerifiedFilter,
      boxDescFilter,
      isDrawingMode,
      syncStatus,
      syncNow,
      isHydrating,
      compareMode,
      iouThreshold,
      compareVerifiedOnly,
      compareCategoryFilter,
      comparison,
      selectedComparisonId,
      selectComparisonItem,
    ]
  )

  return (
    <LabelerContext.Provider value={value}>{children}</LabelerContext.Provider>
  )
}
